import React, { useState } from 'react'
import { Box, IconButton, Menu, MenuItem } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { MoreHorizontal } from 'react-feather'
import { useReactiveVar } from '@apollo/client'
import { BuildingFragment } from '../../generated/graphql'
import { useBuildings } from '../../building/use-buildings'
import { activeVersionVar, projectVar } from '../../apollo/reactive-vars'
import { useAlert } from '../alert/AlertProvider'
import {
  FormValues,
  mapFormValuesToBuildingInput,
} from './basic-info-dialog/form-values'
import { BuildingBasicInfoDialog } from './basic-info-dialog/BuildingBasicInfoDialog'
import { isValidPart } from '../../hour-series-collection/hour-series-collection-utility'
import { LoadingDialog } from '../common/LoadingDialog'
import { useAuthorization } from '../../authorization/use-authorization'

type Props = {
  building: BuildingFragment
  onClose: () => void
}

export function BuildingOptions({
  building,
  onClose,
}: Props): React.ReactElement {
  const project = useReactiveVar(projectVar)
  const activeVersion = useReactiveVar(activeVersionVar)
  const { updateBuilding, deleteBuilding, isUpdateLoading } = useBuildings()
  const { alert } = useAlert()
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLElement | null>(
    null,
  )
  const canEditProject = useAuthorization().canEditProject(project)

  if (!activeVersion) {
    return <div />
  }

  const partFormValues = building.parts.filter(isValidPart).map((part) => ({
    id: part.id,
    name: part.name,
    area: part.partArea.toString(),
  }))

  const onEditSubmit = async (values: FormValues) => {
    setIsEditing(false)

    const input = mapFormValuesToBuildingInput(values, true)
    await updateBuilding(building.id, input)
  }

  const onEditStart = () => {
    setMenuAnchorEl(null)
    setIsEditing(true)
  }

  const onDelete = () => {
    setMenuAnchorEl(null)

    alert(
      t('building.deleteDialog.title'),
      t('building.deleteDialog.message'),
      [
        { title: t('common.cancel') },
        {
          title: t('building.deleteDialog.confirm'),
          color: 'secondary',
          onPress: async () => {
            await deleteBuilding(activeVersion, building.id)
            onClose()
          },
        },
      ],
    )
  }

  return (
    <Box>
      <IconButton onClick={(event) => setMenuAnchorEl(event.currentTarget)}>
        <MoreHorizontal />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        keepMounted
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem onClick={onEditStart} disabled={!canEditProject}>
          {t('building.edit')}
        </MenuItem>
        <MenuItem onClick={onDelete} disabled={!canEditProject}>
          {t('building.delete')}
        </MenuItem>
      </Menu>

      <BuildingBasicInfoDialog
        isOpen={isEditing}
        defaultValues={{
          name: building.name,
          area: building.area.toString(),
          type: building.type,
          multiplier: building.multiplier.toString(),
          demandHourSeriesIds: building.demands.flatMap(
            (demand) => demand.hourSeries.id,
          ),
          createdParts: [],
          updatedParts: partFormValues,
        }}
        onClose={() => setIsEditing(false)}
        onSubmit={onEditSubmit}
      />

      <LoadingDialog isLoading={isUpdateLoading} />
    </Box>
  )
}
